import React, { useEffect, useRef, useState } from 'react';
import '../styles/FeedbackWidget.css';

const MIN_MESSAGE_LENGTH = 6;

function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({ message: '', name: '', email: '' });
  const [status, setStatus] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const textareaRef = useRef(null);
  const dragStateRef = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moved: false
  });
  const ignoreClickRef = useRef(false);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (status.type) {
      setStatus({ type: '', text: '' });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedMessage = form.message.trim();
      if (trimmedMessage.length < MIN_MESSAGE_LENGTH) {
      setStatus({ type: 'error', text: 'Please enter at least 6 characters of feedback.' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', text: '' });

    try {
      const payload = {
        message: trimmedMessage,
        name: form.name.trim(),
        email: form.email.trim(),
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        userId: localStorage.getItem('matai_user_id') || ''
      };

      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      setStatus({ type: 'success', text: 'Thanks! We have received your feedback.' });
      setForm((prev) => ({ ...prev, message: '' }));
    } catch (error) {
      console.error('Feedback submit error:', error);
      setStatus({ type: 'error', text: 'Submission failed. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const endDrag = () => {
    const dragState = dragStateRef.current;
    dragState.dragging = false;
    ignoreClickRef.current = dragState.moved;
    dragState.moved = false;
    window.removeEventListener('pointermove', handleDragMove);
    window.removeEventListener('pointerup', endDrag);
  };

  const handleDragMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState.dragging) return;
    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;
    if (!dragState.moved && Math.hypot(deltaX, deltaY) > 4) {
      dragState.moved = true;
    }
    setDragOffset({
      x: dragState.originX + deltaX,
      y: dragState.originY + deltaY
    });
  };

  const startDrag = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const dragState = dragStateRef.current;
    dragState.dragging = true;
    dragState.startX = event.clientX;
    dragState.startY = event.clientY;
    dragState.originX = dragOffset.x;
    dragState.originY = dragOffset.y;
    dragState.moved = false;
    window.addEventListener('pointermove', handleDragMove);
    window.addEventListener('pointerup', endDrag);
  };

  const handleToggle = () => {
    if (ignoreClickRef.current) {
      ignoreClickRef.current = false;
      return;
    }
    setIsOpen((prev) => !prev);
  };

  return (
    <div
      className="feedback-widget"
      style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}
    >
      {isOpen && (
        <div
          id="feedback-panel"
          className="feedback-panel"
          role="dialog"
          aria-modal="true"
          aria-label="意见反馈"
        >
          <div className="feedback-header feedback-drag-handle" onPointerDown={startDrag}>
            <div>
              <h3>Feedback</h3>
              <p>Tell us your ideas, suggestions, or issues.</p>
            </div>
            <button
              type="button"
              className="feedback-close"
              onClick={() => setIsOpen(false)}
              onPointerDown={(event) => event.stopPropagation()}
              aria-label="Close feedback form"
            >
              ×
            </button>
          </div>
          <form className="feedback-form" onSubmit={handleSubmit}>
            <label className="feedback-field">
              <span>Message</span>
              <textarea
                ref={textareaRef}
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Share your suggestion, idea, or issue..."
                rows={4}
                required
              />
            </label>
            <div className="feedback-row">
              <label className="feedback-field">
                <span>Name (optional)</span>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="So we know how to address you"
                />
              </label>
              <label className="feedback-field">
                <span>Email (optional)</span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="If you'd like a response"
                />
              </label>
            </div>
            {status.type && (
              <div className={`feedback-status ${status.type}`}>{status.text}</div>
            )}
            <button type="submit" className="feedback-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit feedback'}
            </button>
          </form>
        </div>
      )}
      <button
        type="button"
        className={`feedback-toggle feedback-drag-handle ${isOpen ? 'active' : ''}`}
        onPointerDown={startDrag}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-controls="feedback-panel"
      >
        Feedback
      </button>
    </div>
  );
}

export default FeedbackWidget;
